import getSmimeAttachments from './attachments/getSmimeAttachments';
import getEncryptionCertificates from './getEncryptionCertificates';
import isSmimeMessageBccFork from './isSmimeMessageBccFork';
import selectSigningCertificateAndValidate from './selectSigningCertificateAndValidate';
import SmimeCustomError from './SmimeCustomError';
import logSmimeSendSaveActionDatapoint from '../actions/logSmimeSendSaveActionDatapoint';
import setSmimeErrorCode from '../actions/setSmimeErrorCode';
import SmimeContext from '../store/schema/SmimeContext';
import SmimeViewState from '../store/schema/SmimeViewState';
import { AttachmentState } from 'owa-attachment-full-data';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import Message from 'owa-service/lib/contract/Message';
import mimeContentType from 'owa-service/lib/factory/mimeContentType';
import singleRecipientType from 'owa-service/lib/factory/singleRecipientType';
import { lazySmimeAdapter } from 'owa-smime-adapter';
import ISmimeAdapter from 'owa-smime-adapter/lib/adapter/ISmimeAdapter';
import SmimeErrorStateEnum from 'owa-smime-adapter/lib/store/schema/SmimeErrorStateEnum';
import SmimeMessage from 'owa-smime-adapter/lib/store/schema/SmimeMessage';
import SmimeResult from 'owa-smime-adapter/lib/store/schema/SmimeResult';
import SmimeType from 'owa-smime-adapter/lib/store/schema/SmimeType';
import { getStore } from 'owa-smime-adapter/lib/store/store';
import isSmimeAdapterUsable from 'owa-smime-adapter/lib/utils/isSmimeAdapterUsable';
import {
    getBase64Encoded,
    getSmimeItemClassFromSelectedMessageOptions,
    getSmimeTypeFromSelectedMessageOptions,
} from 'owa-smime-adapter/lib/utils/smimeUtils';

export type AddSmimePropertiesRequest = {
    // Current SmimeViewState
    smimeViewState: SmimeViewState;

    // Message object to be encoded to p7m file
    message: Message;

    // From address of the sender
    fromAddress: EmailAddressWrapper;

    // Flag denoting send/save scenario
    isSend: boolean;

    // Compose Id of smime compose
    composeId: string;

    // Array of attachments in compose
    attachments?: AttachmentState[];

    // Array of email addresses of to recipients
    toRecipients: EmailAddressWrapper[];

    // Array of email addresses of cc recipients
    ccRecipients: EmailAddressWrapper[];
};

/**
 * This util will add the MimeContent property to the Item
 * and also remove the body property if needed depending on the S/MIME type.
 * @param addSmimePropertyRequest wrapper request type containing Message, body content and isSend flag.
 */
export default async function tryAddSmimeProperties(
    addSmimePropertyRequest: AddSmimePropertiesRequest
): Promise<void> {
    const {
        smimeViewState,
        message,
        fromAddress,
        isSend,
        composeId,
        attachments,
        toRecipients,
        ccRecipients,
    } = addSmimePropertyRequest;
    const { shouldSignMessageAsSmime, shouldEncryptMessageAsSmime } = smimeViewState;

    let signingCertificate: string = '';
    let encryptionCertificates: string[] = null; /* Initializing with null, so that in save scenario the control will pick the correct encryption certificate.*/

    const smimeType = getSmimeTypeFromSelectedMessageOptions(
        shouldSignMessageAsSmime,
        shouldEncryptMessageAsSmime
    );
    try {
        const smimeAdapter: ISmimeAdapter = await lazySmimeAdapter.import();
        if (isSmimeAdapterUsable()) {
            if (isSend) {
                if (shouldSignMessageAsSmime) {
                    signingCertificate = await selectSigningCertificateAndValidate(
                        smimeAdapter,
                        smimeViewState
                    );
                }

                if (shouldEncryptMessageAsSmime) {
                    encryptionCertificates = await getEncryptionCertificates([
                        ...message.ToRecipients,
                        ...message.CcRecipients,
                        ...message.BccRecipients,
                        fromAddress,
                    ]);
                }
            }
            // Create a copy of item to add from address which is needed by S/MIME extension
            const smimeMessage: SmimeMessage = { ...message, SmimeType: smimeType };
            if (!smimeMessage.From) {
                smimeMessage.From = singleRecipientType({ Mailbox: fromAddress });
            }

            /**
             * In case of the BCC forks while creating the outer message, the To and Cc
             * recipients are not added. Hence we explicity add the To and Cc recipients
             * in the message that will be encoded as p7m, so that while decoding the Bcc
             * recipients will have the information about To and Cc.
             */
            if (isSmimeMessageBccFork(smimeViewState)) {
                smimeMessage.ToRecipients = toRecipients;
                smimeMessage.CcRecipients = ccRecipients;
            }

            smimeMessage.BccRecipients = [];

            smimeMessage.Attachments = getSmimeAttachments(attachments, smimeMessage.Body.Value);
            const createSmimeFromMessageAsyncResponse = await smimeAdapter.createSmimeFromMessageAsync(
                smimeMessage,
                encryptionCertificates,
                signingCertificate
            );
            const smimeResult: SmimeResult = JSON.parse(createSmimeFromMessageAsyncResponse);
            if (smimeResult.ErrorCode == SmimeErrorStateEnum.NoError) {
                // Add MimeContent property to item
                message.MimeContent = mimeContentType({
                    Value: getBase64Encoded(smimeResult.Data as string),
                });

                // Update based on smime type
                if (smimeType !== SmimeType.ClearSigned) {
                    message.Body.Value = '';
                }

                // Update ItemClass based on selected smime message option from the message options dialog
                message.ItemClass = getSmimeItemClassFromSelectedMessageOptions(
                    shouldSignMessageAsSmime,
                    shouldEncryptMessageAsSmime
                );

                logSmimeSendSaveActionDatapoint(composeId, isSend, smimeType);
            } else {
                throw new SmimeCustomError(
                    'S/MIME: Message could not be encoded',
                    smimeResult.ErrorCode,
                    SmimeContext.EncodingMessage
                );
            }
        } else {
            throw new SmimeCustomError(
                'S/MIME: Adapter is not usable. Installation Status: ' +
                    getStore().installationStatus,
                SmimeErrorStateEnum.ControlError,
                SmimeContext.Unspecified
            );
        }
    } catch (e) {
        e.errorCode = e.errorCode || SmimeErrorStateEnum.UnknownError;
        if (!e.errorContext) {
            // Any other generic error apart from smimeCustomError.
            e.errorContext = isSend
                ? SmimeContext.GenericSendingMessage
                : SmimeContext.GenericSavingMessage;
        }
        setSmimeErrorCode(smimeViewState, e.errorCode, e.errorContext);
        logSmimeSendSaveActionDatapoint(composeId, isSend, smimeType, e);
        return Promise.reject(e);
    }
}
